import { Button, IComboBoxOption, Icon } from "@fluentui/react";
import { DataEditor, EditableGridCell, GridCell, GridCellKind, GridColumn, Item } from "@glideapps/glide-data-grid";
import { t } from "i18next";
import React, { useCallback, useEffect, useState } from "react";
import { DropdownCell, DropdownRenderer } from "../../lib/GlideDataGrid/ComboxGlideGrid";
import TimeTaxSupportSystemClient from "../../system";
import { v4 as uuidv4 } from 'uuid';
import { IComboBoxOptionGrid } from "../CargaPorTabla/InterfaceTabla";
import { User } from "../../system/User";

interface ISaleDetail {
  companies: IComboBoxOption[];
  system?: TimeTaxSupportSystemClient;
  user?:User;
  setviewcreate: (data: number) => void;
  setToastExit: (data: boolean) => void;
  changeBlockUI: (state: boolean) => void;
}

interface ISaleCompany {
  empven_id: string;
  empven_emp_id: number;
  empven_nombre_emp: string;
  empven_valor_venta: string;
  empven_anio: string;
  empven_month: string;
  empven_monedaid: number;
}

export const SaleDetailView = (props:ISaleDetail) => {

    const [data, setData] = useState<ISaleCompany[]>([]);
    const [toastError, setToasError] = useState(false);
    const [columns, setColumns] = useState<GridColumn[]>([
      {
        id: "empven_nombre_emp",
        title: t("Empresa (*)"),
        width: 450,
      },
      {
        id: "moneda",
        title: t("Moneda (*)"),
        width: 200,
      },
      {
        id: "empven_valor_venta",
        title: t("Valor de venta (*)"),
        width: 250,
      },
      {
        id: "empven_anio",
        title: t("Año (*)"),
        width: 300,
      },
      {
        id: "empven_month",
        title: t("Mes (*)"),
        width: 250,
      },
    ]);

    const months = [
      { value: 1, label: t("Enero") },
      { value: 2, label: t("Febrero") },
      { value: 3, label: t("Marzo") },
      { value: 4, label: t("Abril") },
      { value: 5, label: t("Mayo") },
      { value: 6, label: t("Junio") },
      { value: 7, label: t("Julio") },
      { value: 8, label: t("Agosto") },
      { value: 9, label: t("Septiembre") },
      { value: 10, label: t("Octubre") },
      { value: 11, label: t("Noviembre") },
      { value: 12, label: t("Diciembre") },
    ]

    const typeCoins = [
      { value: 1, label: t("Sol") },
      { value: 2, label: t("Dolar") },
    ]

    const getCellContent = ([col, row]: Item): GridCell => {
      const item = data[row] || {};

      switch (col) {
        case 0:
          return {
            kind: GridCellKind.Text,
            data: item.empven_nombre_emp || "",
            displayData: item.empven_nombre_emp || "",
            allowOverlay: false,
          };
        case 1:
          return {
            allowOverlay: true,
            kind: GridCellKind.Custom,
            cursor: "pointer",
            data: {
              kind: "dropdown-cell",
              value: {
                value: item.empven_monedaid,
                label: typeCoins.find(coin => coin.value === item.empven_monedaid)?.label || 1,
              },
              displayData: item.empven_monedaid || "00",
              options: typeCoins,
              placeholder: "Seleccionar moneda",
              id: item.empven_id,
              width: "150px",
              alignContent: "center",
              onChange: handleChangeCoin,
            },
          } as DropdownCell;
        case 2:
          const value = Number(item.empven_valor_venta ?? 0);
          return {
            kind: GridCellKind.Number,
            data: parseFloat(item.empven_valor_venta),
            displayData: `${statusCoin(item.empven_monedaid)}  ${formatNumber(value)}`,
            allowOverlay: true,
          };
        case 3:
          return {
            kind: GridCellKind.Number,
            data: parseInt(item.empven_anio) || 0,
            displayData: item.empven_anio || "",
            allowOverlay: true,
          };
        case 4:
          return {
            allowOverlay: true,
            kind: GridCellKind.Custom,
            cursor: "pointer",
            data: {
              kind: "dropdown-cell",
              value: {
                value: item.empven_month,
                label: months.find(month => month.value === parseInt(item.empven_month))?.label || "",
              },
              displayData: item.empven_month || "00",
              options: months,
              placeholder: "Seleccionar mes",
              id: item.empven_id,
              width: "150px",
              alignContent: "center",
              onChange: handleChangeMonth,
            },
          } as DropdownCell;
        default:
          return {
            kind: GridCellKind.Text,
            data: "",
            displayData: "",
            allowOverlay: true,
          };
      }
    };

    const onCellEdited = useCallback(
      (cell: Item, newValue: EditableGridCell) => {
        const [col, row] = cell;
        const column = columns[col];

        if (!column || row >= data.length) return;
        if (!column.id) return;

        const copy = [...data];
        const item = { ...copy[row] };

        item[column.id] = String(newValue.data);
        copy[row] = item;
        setData(copy);
      },
      [columns, data],
    );

    const handleChangeMonth = (newValue: IComboBoxOptionGrid, id: string) => {
      const updatedData = data.map((item) => {
        if (item.empven_id === id) {
          return { ...item, empven_month: String(newValue.value) };
        }
        return item;
      });
      setData(updatedData);
    }

    const statusCoin = (type) => {
      return type == 1 ? "S/. " : "$ ";
    }

    const handleChangeCoin = (newValue: IComboBoxOptionGrid, id: string) => {
      const updatedData = data.map((item) => {
        if (item.empven_id === id) {
          return { ...item, empven_monedaid: Number(newValue.value) };
        }
        return item;
      });
      setData(updatedData);
    }

    const formatEntity = () => {
      return data.map((item) => ({
        empven_no_distribuir: false,
        empven_emp_id: item.empven_emp_id.toString(),
        empven_monedaid: Number(item.empven_monedaid),
        empven_periodo: formatYearMonth(item.empven_anio, item.empven_month),
        empven_valor_venta: item.empven_valor_venta,
        empven_owner_colid: props.user?.getCollaboratorId() ?? "",
        empven_fecha_registro: new Date().toISOString(),
        empven_fecha_actualizacion: new Date().toISOString(),
      }));
    }

    const formatYearMonth = (year: string, month: string): string => {
      const paddedMonth = String(month).padStart(2, "0");
      return `${year}-${paddedMonth}`;
    };

    const formatNumber = (value: number): string => {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const handleSave = async () => {

      const hasInvalid = data.some(
        (item) =>
          !item.empven_emp_id ||
          !item.empven_valor_venta ||
          !item.empven_anio ||
          !item.empven_month
      );

      if (!hasInvalid) {
        const formatEntiyToPersist = formatEntity();
        props.changeBlockUI(true);
        await props.system?.getCompanySystem().registerEmpresaVentasDriver(formatEntiyToPersist);
        props.changeBlockUI(false);
        props.setviewcreate(0);
        props.setToastExit(true);
      } else {
        setToasError(true);
      }

    }

    const validateCell = useCallback((cell: Item, newValue: EditableGridCell) => {
      if (newValue.kind === GridCellKind.Text) {
        return /^[0-9]*$/.test(String(newValue.data));
      }
      return true;
    }, []);

    useEffect(() => {
      setData(props.companies.map((company:any) => ({
        empven_id: uuidv4(),
        empven_emp_id: company.getId(),
        empven_monedaid: 1,
        empven_nombre_emp: company.getName(),
        empven_valor_venta: "",
        empven_anio: "",
        empven_month: "",
      })));
    }, [props.companies]);

    return (
      <>
        {toastError && (
          <div className="toast_container_collaborator_error">
            <div className="toast_message_collaborator_error">
              <Icon
                iconName="ErrorBadge"
                className="icon_toast_collaborator_error"
                style={{ fontSize: "17px" }}
              />
              {t("Complete todo los campos requeridos")}
            </div>
            <Icon iconName="Clear" onClick={() => setToasError(false)} />
          </div>
        )}
        <div className="collaborator_create_hader_buton">
          <span>
            <Icon
              iconName="ReminderPerson"
              style={{ color: "#0078D4", marginRight: "5px" }}
            />
            <span>{t("Ventas")}</span>
          </span>
          <Button
            className="Button_view"
            type="button"
            onClick={() => handleSave()}
          >
            <Icon iconName="save" style={{ marginRight: "5px" }} />
            <span>{t("Guardar")}</span>
          </Button>
          <Button
            className="Button_back"
            onClick={() => props.setviewcreate(0)}
          >
            <Icon
              iconName="back"
              style={{ color: "#0078D4", marginRight: "3px" }}
            />
            <span>{t("Volver")}</span>
          </Button>
        </div>
        <div>
          <div className="body_nuevo_colaborador">
            <Icon iconName="BIDashboard" style={{ fontSize: "20px" }} />
            <p>{t("Tabla de registro:")}</p>
          </div>
          <div style={{ margin: "15px" }}>
            <DataEditor
              customRenderers={[DropdownRenderer]}
              getCellContent={getCellContent}
              columns={columns}
              rows={props.companies.length}
              smoothScrollX
              smoothScrollY
              rowSelect="multi"
              columnSelect="multi"
              onCellEdited={onCellEdited}
              validateCell={validateCell}
              keybindings={{
                selectAll: true,
                copy: true,
                paste: true,
                delete: true,
              }}
              getCellsForSelection={true}
              width={"300"}
            />
          </div>
        </div>
      </>
    );
}